/**
 * useComposition Hook
 *
 * Manages workflow composition state and GraphQL operations for DRR-062.
 * Sends NAICS classification + tenant metadata to the LLM composition engine,
 * which determines the appropriate workflows, rules, and compliance frameworks.
 *
 * @example
 * ```tsx
 * const { createWorkflows, loading, error } = useComposition();
 *
 * const result = await createWorkflows('tenant-123', {
 *   industry: '62',
 *   state: 'MN',
 *   naicsCode: '623',
 *   payers: ['medicaid_ew'],
 * });
 * ```
 */

import { useState, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- Data-layer hook, not UI component
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Operations
// ============================================================================

const CREATE_TENANT_WORKFLOWS = gql`
  mutation CreateTenantWorkflows($tenantId: String!, $metadata: TenantMetadataInput!) {
    createTenantWorkflows(tenantId: $tenantId, metadata: $metadata) {
      success
      workflowId
      instanceId
      taskCount
      error
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Industry identifier — NAICS sector code (e.g., '62', '52', '54').
 */
export type Industry = string;

export type Payer =
  | 'private_pay'
  | 'medicaid_ew'
  | 'medicaid_cadi'
  | 'medicaid_dd'
  | 'medicare'
  | 'va'
  | 'commercial_insurance';

export interface TenantMetadataInput {
  industry: Industry;
  state: string;
  naicsCode?: string;
  city?: string;
  payers?: Payer[];
  services?: string[];
  /** Accepted regulatory nodes from the Regulatory Profile step (DRR-075). */
  regulatoryNodes?: Array<{ nodeId: string; edgeType: string }>;
  customFields?: Record<string, unknown>;
}

export interface CreateWorkflowsResult {
  success: boolean;
  workflowId: string;
  instanceId?: string;
  taskCount: number;
  error?: string;
}

export interface UseCompositionReturn {
  /** Create workflows for tenant — triggers LLM composition + generation */
  createWorkflows: (
    tenantId: string,
    metadata: TenantMetadataInput
  ) => Promise<CreateWorkflowsResult | null>;

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Clear error */
  clearError: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useComposition(): UseCompositionReturn {
  const [error, setError] = useState<string | null>(null);

  const [executeMutation, { loading }] = useMutation<Record<string, any>>(CREATE_TENANT_WORKFLOWS);

  const createWorkflows = useCallback(
    async (
      tenantId: string,
      metadata: TenantMetadataInput
    ): Promise<CreateWorkflowsResult | null> => {
      setError(null);
      try {
        const { data } = await executeMutation({
          variables: { tenantId, metadata },
        });

        const result = data?.createTenantWorkflows;
        if (result?.error) {
          setError(result.error);
          return null;
        }

        return result ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create workflows';
        setError(message);
        return null;
      }
    },
    [executeMutation]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    createWorkflows,
    loading,
    error,
    clearError,
  };
}

// ============================================================================
// Payer Configuration
// ============================================================================

export interface PayerConfig {
  id: Payer;
  label: string;
  description: string;
  requiresEnrollment: boolean;
}

export const PAYERS: PayerConfig[] = [
  {
    id: 'private_pay',
    label: 'Private Pay',
    description: 'Clients pay out of pocket',
    requiresEnrollment: false,
  },
  {
    id: 'medicaid_ew',
    label: 'Medicaid EW (Elderly Waiver)',
    description: 'Minnesota Medicaid waiver for elderly care',
    requiresEnrollment: true,
  },
  {
    id: 'medicaid_cadi',
    label: 'Medicaid CADI',
    description: 'Community Alternatives for Disabled Individuals',
    requiresEnrollment: true,
  },
  {
    id: 'medicaid_dd',
    label: 'Medicaid DD Waiver',
    description: 'Developmental Disabilities waiver',
    requiresEnrollment: true,
  },
  {
    id: 'medicare',
    label: 'Medicare',
    description: 'Federal Medicare program',
    requiresEnrollment: true,
  },
  {
    id: 'va',
    label: 'VA (Veterans Affairs)',
    description: 'Veterans Affairs healthcare benefits',
    requiresEnrollment: true,
  },
  {
    id: 'commercial_insurance',
    label: 'Commercial Insurance',
    description: 'Private health insurance providers',
    requiresEnrollment: true,
  },
];

// US States for dropdown
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
] as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get payer config by ID
 */
export function getPayerConfig(payer: Payer): PayerConfig | undefined {
  return PAYERS.find((p) => p.id === payer);
}

// Stub types and helpers for tenant-setup features
export type ServiceType = string;
export const CompositionEngineType = {
  SagaStateMachine: 'SagaStateMachine',
  DagWorkflow: 'DagWorkflow',
  StateMachine: 'StateMachine',
  ZenEngine: 'ZenEngine',
  ActorRuntime: 'ActorRuntime',
} as const;
export type CompositionEngineType = string;
export interface CompositionPreviewResult {
  workflows: any[];
  rules: any[];
  dependencies: any[];
  integrations: any[];
  recommendedEngine?: string;
  totalStates?: number;
  estimatedDurationDays?: number;
  isLifeCritical?: boolean;
  matchedRules?: any[];
  rulesEvaluated?: number;
  workflowIds?: string[];
  subWorkflows?: any[];
}

export function getEngineLabel(engine: CompositionEngineType): string {
  return engine;
}

export function getEngineDescription(engine: CompositionEngineType): string {
  return `Engine: ${engine}`;
}

export function getIndustryConfig(industry: string): { label: string; services: ServiceType[]; serviceTypes: ServiceType[] } {
  return { label: industry, services: [], serviceTypes: [] };
}
